import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, API, getReportSec } from "@/lib/api";

const fresh = (cur, next) => (!cur || next?.report_sec === getReportSec() || cur.report_sec !== getReportSec() ? next : cur);

const AppCtx = createContext(null);
const EMPTY = { settings: null, churches: [], options: { categories: [], collections: [], destinations: [], giver_types: [], jemdron: [] }, events: [], regions: [] };
const USER_SECTIONS = ["tithes", "progress", "donations", "conference", "reports"];

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [data, setData] = useState(EMPTY);
  const [unread, setUnread] = useState(0);
  const refreshUnread = useCallback(() => api.get("/messages/unread").then((r) => setUnread(r.data.count)).catch(() => {}), []);

  useEffect(() => {
    if (!user || user.must_change_password) { setUnread(0); return undefined; }
    refreshUnread();
    const t = setInterval(refreshUnread, 15000);
    window.addEventListener("focus", refreshUnread);
    return () => { clearInterval(t); window.removeEventListener("focus", refreshUnread); };
  }, [user, refreshUnread]);


  const reload = useCallback(async () => {
    const [s, c, cat, col, dest, ev, rg, gt, jd] = await Promise.all([
      api.get("/settings"), api.get("/churches"), api.get("/options/categories"),
      api.get("/options/collections"), api.get("/options/destinations"), api.get("/events").catch(() => ({ data: [] })), api.get("/regions").catch(() => ({ data: [] })), api.get("/options/giver_types").catch(() => ({ data: [] })), api.get("/options/jemdron").catch(() => ({ data: [] })),
    ]);
    setData((d) => ({ settings: fresh(d.settings, s.data), churches: c.data, options: { categories: cat.data, collections: col.data, destinations: dest.data, giver_types: gt.data, jemdron: jd.data }, events: ev.data, regions: rg.data }));
  }, []);

  useEffect(() => {
    const onSec = () => { if (user) { api.get("/settings").then((r) => setData((d) => ({ ...d, settings: fresh(d.settings, r.data) }))).catch(() => {}); } };
    window.addEventListener("mwjk-rsec", onSec);
    return () => { window.removeEventListener("mwjk-rsec", onSec); };
  }, [user]);

  useEffect(() => {
    api.get("/auth/me").then((r) => { localStorage.setItem("mwjk-user", JSON.stringify(r.data)); setUser(r.data); }).catch((e) => {
      const cached = !e.response && localStorage.getItem("mwjk-user");
      setUser(cached ? JSON.parse(cached) : false);
    });
    const onLogout = () => { localStorage.removeItem("mwjk-user"); localStorage.removeItem("mwjk-get-cache"); setUser(false); };
    window.addEventListener("mwjk-logout", onLogout);
    return () => window.removeEventListener("mwjk-logout", onLogout);
  }, []);

  useEffect(() => {
    if (user) { reload().catch(() => {}); } else { setData(EMPTY); }
  }, [user, reload]);

  useEffect(() => {
    if (!user) return undefined;
    const check = () => { if (navigator.onLine) { api.get("/auth/me").catch(() => {}); } };
    const t = setInterval(check, 20000);
    const onVis = () => { if (document.visibilityState === "visible") { check(); } };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", onVis); };
  }, [user]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("mwjk-user", JSON.stringify(r.data));
    setUser(r.data);
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("mwjk-user");
    localStorage.removeItem("mwjk-get-cache");
    setUser(false);
  };
  const refreshMe = useCallback(() => api.get("/auth/me").then((r) => setUser(r.data)), []);
  const [logoUrl, setLogoUrl] = useState("/logo192.png");
  const refreshLogo = useCallback(() => api.get("/public/branding").then((r) => setLogoUrl(r.data.logo_id ? `${API}/public/images/${r.data.logo_id}` : "/logo192.png")).catch(() => {}), []);
  useEffect(() => { refreshLogo(); }, [refreshLogo]);
  const [loginStyle, setLoginStyle] = useState(null);
  const refreshLoginStyle = useCallback(() => api.get("/public/login-style").then((r) => setLoginStyle(r.data)).catch(() => {}), []);
  useEffect(() => { refreshLoginStyle(); }, [refreshLoginStyle]);
  useEffect(() => {
    const root = document.documentElement;
    if (loginStyle?.site_bg) { root.style.setProperty("--site-bg", loginStyle.site_bg); } else { root.style.removeProperty("--site-bg"); }
  }, [loginStyle]);

  const value = useMemo(() => {
    const isAdmin = !!user && user.role === "master_admin";
    const areas = isAdmin ? (user.admin_areas?.length ? user.admin_areas : ["all"]) : [];
    const hasArea = (...a) => areas.includes("all") || a.some((x) => areas.includes(x));
    return {
      ...data, user, isAdmin, login, logout, reload, refreshMe, hasArea, unread, refreshUnread, logoUrl, refreshLogo, loginStyle, refreshLoginStyle, isFullAdmin: areas.includes("all"),
      myChurch: user ? data.churches.find((c) => c.id === user.church_id) : null,
      featureOn: (k) => data.settings?.features?.[k] !== false && (isAdmin || !USER_SECTIONS.includes(k) || !Array.isArray(user?.sections) || user.sections.includes(k)),
      feat: (k) => data.settings?.features?.[k] !== false && (isAdmin || data.settings?.features?.[`${k}_church`] !== false),
      can: (p) => isAdmin || !!data.settings?.permissions?.[p],
      canEditRec: areas.includes("all") || !!data.settings?.permissions?.[isAdmin ? "admin_can_edit" : "church_can_edit"],
      canBulk: areas.includes("all"),
    };
  }, [data, user, reload, refreshMe, unread, refreshUnread, logoUrl, refreshLogo, loginStyle, refreshLoginStyle]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export const useApp = () => useContext(AppCtx);
